import { quote as priceQuote, type StrategyParams } from '@pricing';
import type { Zone } from '../api-client.ts';

export function formatZonePricing(zone: Pick<Zone, 'pricing'>): string {
  const p = zone.pricing;
  if (!p) return '未定價';
  switch (p.unit) {
    case 'per_hour_per_person':
      return `${p.currency} ${p.amount} / 人 / 小時`;
    case 'per_hour_per_seat':
      return `${p.currency} ${p.amount} / 格 / 小時`;
    case 'flat_per_hour':
      return `${p.currency} ${p.amount} / 小時（整場）`;
    case 'tiered_per_person': {
      if (!p.tiers || p.tiers.length === 0) return '階梯計價';
      const firstAmount = p.tiers[0]?.amountPerPersonPerHour ?? p.amount;
      const lastAmount = p.tiers[p.tiers.length - 1]?.amountPerPersonPerHour ?? p.amount;
      if (firstAmount === lastAmount) {
        return `${p.currency} ${firstAmount} / 人 / 小時`;
      }
      return `${p.currency} ${lastAmount}–${firstAmount} / 人 / 小時（依人數）`;
    }
    default:
      return `${p.currency} ${p.amount}`;
  }
}

export function formatZoneCapacityRange(
  zone: Pick<Zone, 'unitCapacityMin' | 'unitCapacityMax'>,
  perUnitLabel: string,
): string {
  if (zone.unitCapacityMin === zone.unitCapacityMax) {
    return `每${perUnitLabel} 容納 ${zone.unitCapacityMax} 人`;
  }
  return `每${perUnitLabel} 容納 ${zone.unitCapacityMin}–${zone.unitCapacityMax} 人`;
}

export function formatPartySizeRange(
  zone: Pick<Zone, 'minPartySize' | 'maxPartySize'>,
): string {
  return `單場 ${zone.minPartySize}–${zone.maxPartySize} 人`;
}

/**
 * UI-side 估算訂位金額 — 委派給 @pricing SSOT。server 與 client 計算
 * byte-equal（同一份 strategy registry）。
 *
 * legacy `{ amount, unit, tiers }` shape 在此 adapter 轉成 v2 strategy
 * 形式再算。reservation 沒有 rounding policy；billable minutes = 精確
 * window 長度（fractional OK）。
 */
export function estimateReservationPrice(params: {
  pricing: Zone['pricing'];
  partySize: number;
  durationMinutes: number;
  resourceCapacity: number;
}): { amount: number; currency: string } | null {
  const { pricing, partySize, durationMinutes, resourceCapacity } = params;
  if (!pricing) return null;

  let spec: StrategyParams | null = null;
  switch (pricing.unit) {
    case 'per_hour_per_person':
      spec = {
        type: 'hourly_per_person',
        params: { amountPerPersonPerHour: pricing.amount },
      };
      break;
    case 'per_hour_per_seat':
      spec = {
        type: 'hourly_per_seat',
        params: { amountPerSeatPerHour: pricing.amount, seatCount: resourceCapacity },
      };
      break;
    case 'flat_per_hour':
      spec = {
        type: 'flat_per_hour',
        params: { amountPerHour: pricing.amount },
      };
      break;
    case 'tiered_per_person':
      if (pricing.tiers && pricing.tiers.length > 0) {
        spec = {
          type: 'tiered_per_person',
          params: { tiers: pricing.tiers },
        };
      }
      break;
  }
  if (!spec) return null;

  try {
    const money = priceQuote(spec, {
      billableDurationMinutes: durationMinutes,
      partySize,
      startedAt: 0 as never,
    });
    return { amount: money.amount, currency: pricing.currency };
  } catch {
    return null;
  }
}
